/*
  Scale · Carousel
  Small scroll-snap carousel used by the Scale sections.

  Markup:
  <scale-carousel data-autoplay="5">           seconds, optional
    <div data-carousel-track> …slides… </div>
    <button data-carousel-prev></button>       optional
    <button data-carousel-next></button>       optional
    <button data-carousel-dot></button> …      optional, one per slide
  </scale-carousel>

  - Swiping/scrolling is native CSS scroll-snap; this script only adds
    buttons, dots and autoplay.
  - Autoplay only runs while the track actually scrolls (e.g. on mobile),
    stops on the first user interaction and respects reduced motion.
*/
if (!customElements.get('scale-carousel')) {
  customElements.define(
    'scale-carousel',
    class ScaleCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-carousel-track]');
        if (!this.track) return;

        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        this.prevButton = this.querySelector('[data-carousel-prev]');
        this.nextButton = this.querySelector('[data-carousel-next]');

        this.prevButton?.addEventListener('click', () => this.step(-1));
        this.nextButton?.addEventListener('click', () => this.step(1));
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.update();
          });
        };
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        ['pointerdown', 'touchstart', 'focusin', 'wheel'].forEach((type) =>
          this.addEventListener(type, () => this.stopAutoplay(), { passive: true, once: true })
        );

        this.startAutoplay();
        this.update();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        this.track?.removeEventListener('scroll', this.onScroll);
      }

      get slides() {
        return Array.from(this.track.children);
      }

      isScrollable() {
        return this.track.scrollWidth > this.track.clientWidth + 1;
      }

      currentIndex() {
        const left = this.track.scrollLeft;
        let closest = 0;
        this.slides.forEach((slide, index) => {
          if (Math.abs(slide.offsetLeft - left) < Math.abs(this.slides[closest].offsetLeft - left)) closest = index;
        });
        return closest;
      }

      goTo(index) {
        const slide = this.slides[index];
        if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }

      step(direction) {
        const last = this.slides.length - 1;
        let next = this.currentIndex() + direction;
        if (next > last) next = 0;
        if (next < 0) next = last;
        this.goTo(next);
      }

      update() {
        const index = this.currentIndex();
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));

        const atStart = this.track.scrollLeft <= 1;
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
        if (this.prevButton) this.prevButton.disabled = atStart;
        if (this.nextButton) this.nextButton.disabled = atEnd;
        this.classList.toggle('is-scrollable', this.isScrollable());
      }

      startAutoplay() {
        const seconds = Number(this.dataset.autoplay);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!seconds || reducedMotion) return;

        this.timer = setInterval(() => {
          if (this.isScrollable() && !document.hidden) this.step(1);
        }, seconds * 1000);
      }

      stopAutoplay() {
        clearInterval(this.timer);
      }
    }
  );
}
